import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { appointmentKeys } from "./appointment.keys";
import { useApi } from "../../api/ApiProvider";
import { WrappedApiClient } from "../../api/proxiedClient";
import {
  AppointmentApi,
  UpdateCancelledAppointmentDto,
  CancelledAppointmentDto,
  CancellationHistoryItemDto,
  AppointmentDto,
  ContinuousAppointmentDto,
  TimeSlotDisplayDto,
  TimeSlotDto,
  PatientAvailabilityDto,
  AppointmentQueryFilters,
  WaitListFilters,
  WaitListResponse,
  PatientTherapistPreferenceDto,
} from "../../api-client";

export const useAppointmentsByRx = (rxId: string) => {
  const { appointments: appApi } = useApi();
  return useQuery<AppointmentDto[]>({
    queryKey: appointmentKeys.appointmentsByRx.detail(rxId),
    queryFn: () => appApi.getAppointmentsForRx(rxId),
    enabled: !!rxId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Termine für Rx konnten nicht geladen werden" },
  });
};

export const useAppointmentsBySeries = (seriesId: string) => {
  const { appointments: appApi } = useApi();
  return useQuery<AppointmentDto[]>({
    queryKey: appointmentKeys.appointmentsBySeries.detail(seriesId),
    queryFn: () => appApi.getAppointmentsForSeries(seriesId),
    enabled: !!seriesId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Termine für Serie konnten nicht geladen werden" },
  });
};

export const useServiceAppointmentsByPatient = (patientId: number) => {
  const { appointments: appApi } = useApi();
  return useQuery<AppointmentDto[]>({
    queryKey: appointmentKeys.serviceAppointmentsByPatient.detail(patientId),
    queryFn: () => appApi.getSalesAppointmentsForPatient(patientId),
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Service-Termine konnten nicht geladen werden" },
  });
};

export const useCancelledAppointments = (locationId?: string) => {
  const { appointments: appApi } = useApi();
  return useQuery<CancelledAppointmentDto[]>({
    queryKey: appointmentKeys.cancelledAppointments.detail(locationId!),
    queryFn: () => appApi.getCancelledAppointments(locationId!),
    enabled: !!locationId,
    meta: { errorMessage: "Patient konnte nicht geladen werden" },
  });
};

export const useCancelledAppointmentForAppointmentId = (appointmentId?: string) => {
  const { appointments: appApi } = useApi();
  return useQuery<CancelledAppointmentDto>({
    queryKey: appointmentKeys.cancelledAppointments.detail(appointmentId!),
    queryFn: () => appApi.getCancelledAppointmentForAppointmentId(appointmentId!),
    enabled: !!appointmentId,
    staleTime: 3 * 60 * 1000, // 3 minutes
    meta: { errorMessage: "Termine konnten nicht geladen werden" },
  });
};

export const useUpdateCancelledAppointment = (locationId: string) => {
  const { appointments } = useApi();
  const qc = useQueryClient();

  return useMutation({
    // accept an object with id and dto so callers can pass both
    mutationFn: (payload: { id: string; dto: UpdateCancelledAppointmentDto }) =>
      appointments.updateCancelledAppointment(payload.id, payload.dto),
    meta: {
      successMessage: "Stornierung erfolgreich aktualisiert!",
      errorMessage: "Stornierung konnte nicht aktualisiert werden!",
    },
    onSuccess: () => {
      // invalidate all cancelledAppointments queries (partial key)
      qc.invalidateQueries({ queryKey: appointmentKeys.cancelledAppointments.detail(locationId) });
    },
  });
};

export const usePreviewSwapAppointments = (seriesIds: string[]) => {
  // console.log("usePreviewSwapAppointments called with seriesIds:", seriesIds);
  const { appointments: appApi } = useApi();
  return useQuery({
    queryKey: appointmentKeys.previewSwapAppointments.detail(seriesIds),
    queryFn: async () => {
      return await appApi.previewSwapAppointments(seriesIds);
    },
    enabled: seriesIds?.length > 0,
    meta: { errorMessage: "Vorschau der Tauschtermine konnte nicht geladen werden" },
  });
};

/**
 * Imperative bulk fetch for TimeSlotDisplay data. Can be used outside React components.
 * Checks individual caches first, batch-fetches only missing IDs, seeds per-timeslot caches.
 */
export async function getTimeSlotDisplayDataBulk(
  queryClient: QueryClient,
  appointmentsApi: WrappedApiClient<AppointmentApi>,
  timeSlotIds: string[],
): Promise<TimeSlotDisplayDto[]> {
  if (timeSlotIds.length === 0) return [];

  const cached: TimeSlotDisplayDto[] = [];
  const missing: string[] = [];

  for (const id of timeSlotIds) {
    const existing = queryClient.getQueryData<TimeSlotDisplayDto>(
      appointmentKeys.timeSlotDisplay.detail(id),
    );
    if (existing) {
      cached.push(existing);
    } else {
      missing.push(id);
    }
  }

  let fetched: TimeSlotDisplayDto[] = [];
  if (missing.length > 0) {
    const response = await appointmentsApi.getTreatmentDisplayDataBulk({ timeSlotIds: missing });
    fetched = response?.results ?? [];

    for (const dto of fetched) {
      queryClient.setQueryData(appointmentKeys.timeSlotDisplay.detail(dto.id), dto);
    }
  }

  return [...cached, ...fetched];
}

export const useTimeSlotDisplayData = (timeSlotId: string, enabled: boolean = true) => {
  const { appointments: appApi } = useApi();

  return useQuery<TimeSlotDisplayDto>({
    queryKey: appointmentKeys.timeSlotDisplay.detail(timeSlotId),
    queryFn: () => appApi.getTreatmentDisplayData(timeSlotId),
    enabled: enabled && !!timeSlotId,
    staleTime: 15 * 60 * 1000,
    meta: { errorMessage: "Timeslot-Displaydaten konnten nicht geladen werden" },
  });
};

// and example of how an update mutation could look like

export const useUnscheduledAppointments = (locationId: string | undefined, rank: boolean) => {
  const { appointments: appApi } = useApi();
  return useQuery({
    queryKey: appointmentKeys.unscheduledAppointments.detail(locationId!, rank),
    queryFn: () => appApi.getCancelledAndUnscheduledAppointments(locationId!, rank),
    enabled: !!locationId,
    meta: { errorMessage: "Ungeplante Termine könnten nicht geladen werden" },
  });
};

export const useAppointmentsByPatient = (patientId: number, addressId?: string) => {
  const { appointments: appApi } = useApi();
  return useQuery<AppointmentDto[]>({
    queryKey: appointmentKeys.appointmentsByPatient.detail(patientId, addressId),
    queryFn: () => appApi.getAppointmentsForPatient(patientId.toString(), addressId),
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Termine für den Patienten konnten nicht geladen werden" },
  });
};

export const useContinuousAppointmentsByPatient = (patientId: number, addressId?: string) => {
  const { appointments: appApi } = useApi();
  return useQuery<ContinuousAppointmentDto[]>({
    queryKey: appointmentKeys.continuousAppointmentsByPatient.detail(
      patientId.toString(),
      addressId,
    ),
    queryFn: () => appApi.getContinuousAppointmentsForPatient(patientId.toString(), addressId),
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Dauertermine für den Patienten konnten nicht geladen werden" },
  });
};

export const useCancelledAppointmentsByPatient = (patientId: number) => {
  const { appointments: appApi } = useApi();
  return useQuery<CancelledAppointmentDto[]>({
    // queryKey: appointmentKeys.cancelledAppointmentsByPatient.detail(patientId.toString()),
    queryKey: patientId
      ? appointmentKeys.cancelledAppointmentsByPatient.detail(patientId)
      : ["cancelledAppointmentsByPatient", { patientId: undefined }],
    queryFn: async () => {
      const response = await appApi.getCancelledAppointmentsForPatient(patientId);
      return response || [];
    },
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Stornierte Termine für den Patienten konnten nicht geladen werden" },
  });
};

export const useCancellationHistoryByPatient = (patientId: number, enabled = true) => {
  const { appointments: appApi } = useApi();
  return useQuery<CancellationHistoryItemDto[]>({
    queryKey: patientId
      ? appointmentKeys.cancellationHistoryByPatient.detail(patientId)
      : ["cancellationHistoryByPatient", { patientId: undefined }],
    queryFn: async () => {
      const response = await appApi.getCancellationHistoryForPatient(patientId);
      return response || [];
    },
    enabled: enabled && !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Stornierungsverlauf konnte nicht geladen werden" },
  });
};

export const useBlockedSlotsByPatient = (patientId: number) => {
  const { appointments: appApi } = useApi();
  return useQuery<TimeSlotDto[]>({
    queryKey: appointmentKeys.blockedSlotsByPatient.detail(patientId.toString()),
    queryFn: async () => {
      const response = await appApi.getBlockedSlotsForPatient(patientId);
      return response || [];
    },
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Blockierte Zeitslots für den Patienten konnten nicht geladen werden" },
  });
};

export const usePatientUnavailabilityByPatient = (patientId: number) => {
  const { appointments: appApi } = useApi();
  return useQuery<PatientAvailabilityDto[]>({
    queryKey: appointmentKeys.patientUnavailabilityByPatient.detail(patientId.toString()),
    queryFn: async () => {
      const response = await appApi.getPatientUnavailabilityForPatient(patientId);
      return response || [];
    },
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Nichtverfügbarkeit für den Patienten konnte nicht geladen werden" },
  });
};

export const useTherapistListByPatient = (
  patientId: number,
  enabled: boolean = true
) => {
  const { appointments: appApi } = useApi();
  return useQuery<PatientTherapistPreferenceDto[]>({
    queryKey: appointmentKeys.therapistListByPatient.detail(patientId?.toString()),
    queryFn: async () => {
      const response = await appApi.getTherapistListForPatient(patientId);
      return response || [];
    },
    enabled: enabled && !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Therapeuten-Listen für den Patienten konnten nicht geladen werden" },
  });
};

export const useInactiveAppointmentsCount = (patientId: number) => {
  const { appointments: appApi } = useApi();
  return useQuery<number>({
    queryKey: appointmentKeys.inactiveAppointmentsCount.detail(patientId.toString()),
    queryFn: async () => appApi.getInActiveAppointmentsCount(patientId),
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Anzahl inaktiver Termine konnte nicht geladen werden" },
  });
};

export const useFilteredAppointmentsByPatient = (
  patientId: number,
  appointmentFilters: AppointmentQueryFilters,
  omitUnscheduled: boolean,
  enabled: boolean,
  addressId?: string,
) => {
  const { appointments: appApi } = useApi();
  return useQuery<AppointmentDto[]>({
    queryKey: appointmentKeys.filteredAppointmentsByPatient.detail(patientId, appointmentFilters),
    queryFn: () =>
      appApi.getFilteredAppointmentsForPatient(
        patientId,
        appointmentFilters,
        addressId,
        omitUnscheduled,
      ),
    enabled: !!patientId && enabled,
    staleTime: 0,
    meta: { errorMessage: "Gefilterte Termine für den Patienten konnten nicht geladen werden" },
  });
};

export const useGetWaitListByRxSeries = (
  event: any,
  filters: WaitListFilters,
  enabled: boolean,
  locationId: string,
) => {
  const { appointments: appApi } = useApi();
  return useQuery<WaitListResponse>({
    queryKey: appointmentKeys.waitListByRxSeries.detail(event.start),
    queryFn: () => appApi.getWaitingListByRxSeries(event.start, locationId, filters),
    meta: { errorMessage: "Warteliste konnte nicht geladen werden" },
    enabled: !!event.start && !!event.resourceId && enabled,
  });
};
